import React, { useState, useRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { allProducts } from '../mock';

const formatPrice = (n) =>
  n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SearchBar = ({ className = '', onResultClick }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const q = query.trim().toLowerCase();
  const results = q.length < 2 ? [] : allProducts.filter((p) => {
    const haystack = `${p.name} ${p.code} ${p.category}`.toLowerCase();
    // Suportă căutare cu mai multe cuvinte cheie (toate trebuie să se potrivească)
    return q.split(/\s+/).every((token) => haystack.includes(token));
  }).slice(0, 12);

  const handleResultClick = (product) => {
    setOpen(false);
    setQuery('');
    if (onResultClick) onResultClick(product);
    // Scroll la categoria produsului
    const el = document.getElementById(product.categoryId);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div ref={ref} className={`relative w-full ${className}`}>
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Caută produse după nume sau cod..."
          className="w-full h-11 pl-4 pr-20 bg-gray-100 border border-gray-200 rounded-full text-sm focus:outline-none focus:border-amber-500 focus:bg-white transition-colors"
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setOpen(false); }}
            className="absolute right-12 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-gray-200 text-gray-500 flex items-center justify-center transition-colors"
            aria-label="Șterge căutarea"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="absolute right-1 top-1 h-9 w-9 rounded-full bg-amber-500 hover:bg-amber-600 text-white flex items-center justify-center transition-colors"
          aria-label="Caută"
        >
          <Search className="w-4 h-4" />
        </button>
      </div>

      {/* Dropdown rezultate */}
      {open && q.length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-md shadow-2xl max-h-[60vh] overflow-y-auto z-50">
          {results.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500">
              Niciun produs găsit pentru <strong className="text-gray-900">"{query}"</strong>
            </div>
          ) : (
            <>
              <div className="px-4 py-2 text-[11px] uppercase tracking-wider text-gray-500 bg-gray-50 border-b border-gray-100 font-semibold">
                {results.length} {results.length === 1 ? 'rezultat' : 'rezultate'}
              </div>
              <ul className="divide-y divide-gray-100">
                {results.map((p) => (
                  <li key={`${p.categoryId}-${p.id}`}>
                    <button
                      type="button"
                      onClick={() => handleResultClick(p)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-amber-50 transition-colors text-left"
                    >
                      <img
                        src={p.image}
                        alt=""
                        className="w-14 h-14 object-cover rounded-sm bg-gray-100 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 line-clamp-2 leading-snug mb-0.5">
                          {p.name}
                        </div>
                        <div className="flex items-center gap-2 text-[11px] text-gray-500">
                          <span className="bg-gray-100 px-1.5 py-0.5 rounded-sm font-medium">{p.category}</span>
                          <span>Cod: {p.code}</span>
                        </div>
                      </div>
                      <div className="text-sm font-bold text-gray-900 flex-shrink-0">
                        {formatPrice(p.price)} lei
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
